import { Injectable } from '@angular/core';
import type { TemplateRef, ViewContainerRef } from '@angular/core';

export type BuildingBannerSlotKey =
    | 'draft'
    | 'ownership-transfer'
    | `route:${string}`;

interface BuildingBannerSlotEntry {
    readonly key: BuildingBannerSlotKey;
    readonly order: number;
    readonly template: TemplateRef<unknown>;
}

@Injectable()
export class BuildingBannerSlotRegistry {
    //#region State
    private slot: ViewContainerRef | null = null;
    private readonly entries = new Map<
        BuildingBannerSlotKey,
        BuildingBannerSlotEntry
    >();
    //#endregion

    //#region Public methods
    public registerSlot(slot: ViewContainerRef): void {
        this.slot = slot;
        this.render();
    }

    public unregisterSlot(slot: ViewContainerRef): void {
        if (this.slot !== slot) return;

        this.slot.clear();
        this.slot = null;
    }

    public setEntry(
        key: BuildingBannerSlotKey,
        template: TemplateRef<unknown> | null,
        order: number
    ): void {
        if (template === null) {
            this.entries.delete(key);
            this.render();
            return;
        }

        this.entries.set(key, {
            key,
            order,
            template,
        });
        this.render();
    }
    //#endregion

    //#region Private methods
    private render(): void {
        if (this.slot === null) return;

        this.slot.clear();

        const entries = Array.from(this.entries.values()).sort(
            (first, second) => first.order - second.order
        );

        for (const entry of entries) {
            const view = this.slot.createEmbeddedView(entry.template);
            view.detectChanges();
        }
    }
    //#endregion
}
